// Thin wrapper around localStorage for the master-data translation cache.
// Each language is stored in its own key so reads + writes touch only one blob.
// The stored value is the raw stringified tree returned by the API (we keep it
// serialised to avoid JSON.parse on every render — components read via the in-memory
// Redux copy; the localStorage copy is just the persistence layer for reloads).

const CACHE_KEY_PREFIX = 'masterLocale::';
const META_KEY = 'masterLocale::__meta';

const safeParse = (raw, fallback) => {
    if (!raw) return fallback;
    try {
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
};

const readMeta = () => safeParse(localStorage.getItem(META_KEY), {});

const writeMeta = (meta) => {
    try {
        localStorage.setItem(META_KEY, JSON.stringify(meta));
    } catch (e) {
        console.warn('masterLocaleStore: meta write failed', e);
    }
};

// Read a cached tree as an object. Returns null if nothing is cached for the language.
export const readCachedLocale = (lcode) => {
    if (!lcode) return null;
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${lcode}`);
    return safeParse(raw, null);
};

// Read the stringified cache directly — useful when you want to skip a parse/stringify
// round-trip (e.g. the admin UI that shows the raw JSON).
export const readCachedLocaleRaw = (lcode) => {
    if (!lcode) return null;
    return localStorage.getItem(`${CACHE_KEY_PREFIX}${lcode}`);
};

// Persist a freshly fetched tree. Accepts either an object or a pre-stringified blob —
// prefer the stringified form since that's what the API returns, saving a re-serialise.
export const writeCachedLocale = (lcode, treeOrString, version = 0) => {
    if (!lcode) return;
    try {
        const payload = typeof treeOrString === 'string' ? treeOrString : JSON.stringify(treeOrString ?? {});
        localStorage.setItem(`${CACHE_KEY_PREFIX}${lcode}`, payload);
        const meta = readMeta();
        meta[lcode] = { version, cachedAt: Date.now() };
        writeMeta(meta);
    } catch (e) {
        console.warn(`masterLocaleStore: write failed for ${lcode}`, e);
    }
};

export const getCachedVersion = (lcode) => readMeta()[lcode]?.version ?? 0;

// Apply an in-place patch to the cached locale for a specific language. Used when the
// admin edits a cell in the Translation master — we update the cache so the next render
// picks up the new value without a full refetch.
export const patchCachedLocaleCell = (lcode, moduleName, entityId, field, value) => {
    if (!lcode || !moduleName || !entityId || !field) return;
    const current = readCachedLocale(lcode) || {};
    if (!current[moduleName]) current[moduleName] = {};
    if (!current[moduleName][entityId]) current[moduleName][entityId] = {};

    if (value === null || value === undefined || value === '') {
        // Cleared cell — remove the field and prune empty parents so the tree stays tight.
        delete current[moduleName][entityId][field];
        if (Object.keys(current[moduleName][entityId]).length === 0) {
            delete current[moduleName][entityId];
        }
        if (Object.keys(current[moduleName]).length === 0) {
            delete current[moduleName];
        }
    } else {
        current[moduleName][entityId][field] = value;
    }

    writeCachedLocale(lcode, current, Date.now());
};

// Remove an entire entity (all fields) from the cached tree — used on key deletion.
export const pruneCachedLocaleEntity = (lcode, moduleName, entityId) => {
    if (!lcode || !moduleName || !entityId) return;
    const current = readCachedLocale(lcode);
    if (!current || !current[moduleName]) return;
    delete current[moduleName][entityId];
    if (Object.keys(current[moduleName]).length === 0) {
        delete current[moduleName];
    }
    writeCachedLocale(lcode, current, Date.now());
};

// Apply a cell-level patch across every cached language at once. Handy for source-
// language updates triggered by master-data create/update flows where only one API call
// happens but the cache for multiple languages may exist locally.
export const patchCachedCellAllLanguages = (moduleName, entityId, field, value) => {
    const meta = readMeta();
    Object.keys(meta).forEach((lcode) => {
        patchCachedLocaleCell(lcode, moduleName, entityId, field, value);
    });
};

// Nuke every cached language — used on logout or when the language master changes
// dramatically.
export const clearAllCachedLocales = () => {
    const meta = readMeta();
    Object.keys(meta).forEach((lcode) => {
        try { localStorage.removeItem(`${CACHE_KEY_PREFIX}${lcode}`); } catch {}
    });
    try { localStorage.removeItem(META_KEY); } catch {}
};

// Resolve a single label from the in-memory tree. Kept as a helper so non-React code
// (e.g. one-off scripts, redux selectors) can resolve without re-implementing the lookup.
export const resolveFromTree = (tree, moduleName, entityId, field, fallback = '') => {
    if (!tree || !moduleName || !entityId || !field) return fallback;
    const value = tree?.[moduleName]?.[entityId]?.[field];
    if (value === undefined || value === null || value === '') return fallback;
    return value;
};
